import { ReactNode } from 'react'

import { SpkFarmGeneralStatsResult } from '../../types'
import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { Info } from '@/ui/molecules/info/Info'
import { UsdAmountTooltip } from '@/ui/organisms/usd-amount-tooltip/UsdAmountTooltip'
import { cn } from '@/ui/utils/style'

export interface GeneralStatsBarProps {
  generalStatsResult: SpkFarmGeneralStatsResult
}

export function GeneralStatsBar({ generalStatsResult }: GeneralStatsBarProps) {
  if (generalStatsResult.isPending) {
    return (
      <BarWrapper>
        <Stat>
          <Skeleton className="w-16" />
        </Stat>
        <Stat>
          <Skeleton className="w-16" />
        </Stat>
        <PoweredBy />
      </BarWrapper>
    )
  }

  if (generalStatsResult.isError) {
    return (
      <BarWrapper>
        <PoweredBy />
      </BarWrapper>
    )
  }

  return (
    <BarWrapper>
      <Stat>
        <Label>TVL:</Label>
        <Value>
          <UsdAmountTooltip token={USD_MOCK_TOKEN} amount={generalStatsResult.data.tvl} style="compact" />
        </Value>
      </Stat>
      <Stat>
        <Label>Users:</Label>
        <Value>{formatUsersNumber(generalStatsResult.data.stakers)}</Value>
      </Stat>
      <PoweredBy />
    </BarWrapper>
  )
}

function Stat({ children }: { children: ReactNode }) {
  return <div className="flex gap-[3px] px-2 first:pl-3 last:pr-3 sm:gap-1 sm:px-4">{children}</div>
}

function Label({ children }: { children: string }) {
  return <div className="typography-label-3 text-secondary">{children}</div>
}

function Value({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('typography-label-3 text-primary', className)}>{children}</div>
}

function formatUsersNumber(users: number): string {
  return Intl.NumberFormat('en-US', { maximumFractionDigits: 0, notation: 'compact' }).format(users)
}

function PoweredBy() {
  return (
    <Stat>
      <Label>Powered by:</Label>
      <Value>
        <div className="flex items-center gap-1">
          <img src={tokenLogos.sky} className="size-3" />
          <div>Sky</div>
          <Info>SPK farming is offered by the Sky ecosystem.</Info>
        </div>
      </Value>
    </Stat>
  )
}

function BarWrapper({ children }: { children: ReactNode }) {
  return (
    <div className={cn('inline-flex divide-x divide-secondary rounded-[10px]', 'bg-primary/80 py-3 backdrop-blur-lg')}>
      {children}
    </div>
  )
}
